(() => {
  /**
   * Shim navigator.language
   * This is from https://gist.github.com/brettz9/6150009
   */
  if (!navigator.language) {
    navigator.language = (navigator.userLanguage && navigator.userLanguage.replace(/-[a-z]{2}$/, String.prototype.toUpperCase)) || 'en-US';
  }

  /**
   * Key at which to store the chosen language in localstorage
   */
  const languageChoiceKey = 'chosen-language';

  /**
   * Array of supported languages; that at index 0 is default
   */
  const supportedLanguages = [
    'en',
    'fr',
    'uk',
  ];

  // switch to this language if they're not in the blacklist
  const nonBlacklistLanguageSwitch = {
    'en': 'uk',
  };

  /**
   * Get the default language
   *
   * @returns {string} Two-letter lowercase language code
   */
  function defaultLanguage() {
    return supportedLanguages[0];
  }

  /**
   * Is a language supported?
   *
   * @param {string} language - Two-letter lowercase language code
   * @returns {boolean}
   */
  const languageSupported = (() => {
    const supported = {};
    for (let i = 0, l = supportedLanguages.length; i < l; i++) {
      supported[supportedLanguages[i]] = true;
    }
    return (language) => !!supported[language];
  })();

  /**
   * Get the currently displayed language, based on the current URL
   *
   * @returns {string} Two-letter lowercase language code
   */
  function currentLanguage() {
    return document.documentElement.lang;
  }

  /**
   * Sometimes languages have a region code (e.g. en_GB), turn that into just 'en'
   *
   * @param {string} language - Language code which may be 2 or 5 characters, of
   * indeterminate case
   * @returns {string} Two-letter lowercase language code
   */
  function normalizeLanguage(language) {
    return language.split(/[_-]+/)[0].toLowerCase();
  }

  /**
   * Get the supported language preferred by the current user (or default); it may
   * not be the same as the current language
   *
   * @returns {string} Two-letter lowercase language code
   */
  function determinePreferredLanguage(isBlacklistedCountry) {
    const storedLanguage = window.localStorage.getItem(languageChoiceKey);

    // If the user has stored a preference, that is the preferred language
    if (storedLanguage) {
      return storedLanguage;
    }

    // Look in the Accept-Languages header (or nearest equivalent) for languages
    // we accept; pick the first match we find
    const acceptedLanguages = (navigator.languages ? navigator.languages : [navigator.language]).map(normalizeLanguage);
    for (let i = 0, l = acceptedLanguages.length; i < l; i++) {
      let acceptedLanguage = acceptedLanguages[i];
      if (languageSupported(acceptedLanguage)) {
        if (!isBlacklistedCountry && nonBlacklistLanguageSwitch[acceptedLanguage]) {
          acceptedLanguage = nonBlacklistLanguageSwitch[acceptedLanguage];
        }
        return acceptedLanguage;
      }
    }

    // No matches; fall back to default language
    return defaultLanguage();
  }

  function redirectToPreferredLanguage() {
    //create a partial function which communicates the isBlacklistedCountry and then redirects
    const cbDetermineThenRedirect = (value) => () => redirectToLanguage(determinePreferredLanguage(value));

    window.ZO.cookieNotice.callbackIfBlackListedCountry({
      yes: cbDetermineThenRedirect(true),
      no: cbDetermineThenRedirect(false),
    })
  }

  /**
   * Store a preferred language
   *
   * @param {string} language - Two-letter lowercase language code
   * @returns {undefined}
   */
  function storePreferredLanguage(language) {
    window.localStorage.setItem(languageChoiceKey, language);
  }

  /**
   * Get the path to the current page in a particular language
   *
   * @param {string} target - Two-letter lowercase language code
   * @returns {(string|null)} URL or null if there is no equivalent
   */
  function languageUrl(target) {
    const current = currentLanguage();

    // Short-circuit if the current language is the target
    if (target === current) {
      return window.location.href;
    }

    // Look for alternate in target language
    const link = document.querySelector('link[rel="alternate"][hreflang="' + target + '"]');
    if (link) {
      return link.href;
    }

    // No equivalent
    return null;
  }

  /**
   * Redirect to the current page in a particular language, or do nothing if that
   * is the current language or there is no alternate in that language
   *
   * @param {string} language - Two-letter lowercase language code
   * @returns {undefined}
   */
  function redirectToLanguage(language) {
    if (language === currentLanguage()) {
      return;
    }
    const url = languageUrl(language);
    if (url == null) {
      return;
    }
    window.location.assign(url);
  }

  /**
   * Handle a language switcher link click
   *
   * @param {Object} event - Click event
   * @returns {undefined}
   */
  function handleLanguageSwitcherClick(event) {
    const a = event.currentTarget;

    const lang = a.hreflang;
    if (!lang) {
      // No hreflang attribute; do nothing
      return;
    }

    // Store this manually chosen language as a preference
    storePreferredLanguage(lang);

    // Follow the link as usual (don't prevent default)
  }

  /**
   * Alter language switcher links to point to equivalent pages where possible
   *
   * This should be run after templates have rendered.
   *
   * @returns {undefined}
   */
  function updateLanguageSwitcherLinks() {
    const links = document.getElementsByClassName('language-link');
    for (let i = 0, l = links.length; i < l; i++) {
      const a = links[i];
      const url = languageUrl(a.hreflang);
      if (url != null) {
        a.href = url;
      }
    }
  }

  /**
   * Global exports
   */
  if (!window.ZO) {
    window.ZO = {};
  }
  window.ZO.language = {
    currentLanguage: currentLanguage,
    defaultLanguage: defaultLanguage,
    handleLanguageSwitcherClick: handleLanguageSwitcherClick,
    languageSupported: languageSupported,
    languageUrl: languageUrl,
    redirectToPreferredLanguage: redirectToPreferredLanguage,
    storePreferredLanguage: storePreferredLanguage,
    updateLanguageSwitcherLinks: updateLanguageSwitcherLinks,
  };
})();
